import React, { ReactElement } from 'react';
import classNames from 'classnames/bind';
import Link from 'next/link';
import styles from 'sections/Resources/PrivacyPolicy/components/SectionContent/SectionContent.module.scss';

const cn = classNames.bind(styles);

const SectionContent = (): ReactElement => {
  return (
    <section className={cn('content')}>
      <div className={cn('container')}>

        {/* INTRODUCTION AND SCOPE */}
        <div className={cn('item')}>
          <h2 className={cn('item__heading')}>
            Introduction and Scope
          </h2>
          <p className={cn('item__paragraph')}>
            DF Alliance FZE, a company registered in the Emirate of Dubai,
            United Arab Emirates, and part of the DP World Group (the "Company" or "we" or "us")
            is committed to protecting and respecting personal data that it handles in connection
            with the services it provides to its users, particularly in compliance with data
            protection law.
          </p>
          <p className={cn('item__paragraph')}>
            This privacy policy describes how we use your personal data when you use our website{' '}
            <Link
              className={cn('item__link')}
              href='https://www.df-alliance.com'
            >
              www.df-alliance.com
            </Link>{' '}
            ("Our Website"),
            any apps we provide (as applicable), email or other correspondence or when we
            provide products or services to you.
          </p>
          <p className={cn('item__paragraph')}>
            You have legal rights to access and to know how we use the personal data that we hold about you.
          </p>
          <p className={cn('item__paragraph')}>
            You can read, print and save this whole policy. We use sub-headings to help you find specific information that you may be looking for more easily.
          </p>
        </div>

        {/* DATA CONTROLLER */}
        <div className={cn('item')}>
          <h2 className={cn('item__heading')}>
            Data Controller
          </h2>
          <p className={cn('item__paragraph')}>
            Our Website is operated under the legal jurisdiction of the Emirate of Dubai,
            United Arab Emirates. For the purpose of the General Data Protection Regulation
            (Regulation EU 2016/679 of the European Parliament and of the Council) (the "GDPR"),
            the Company is the data controller.
          </p>
        </div>

        {/* FURTHER INFORMATION */}
        <div className={cn('item')}>
          <h2 className={cn('item__heading')}>
            Data Controller
          </h2>
          <p className={cn('item__paragraph')}>
            For any further information regarding this Privacy Policy, advice or guidance,
            please email{' '}
            <a
              className={cn('item__link')}
              href='mailto:data.protection@dpworld.com'
            >
              data.protection@dpworld.com
            </a>.
          </p>
        </div>

        {/* INFORMATION WE COLLECT FROM OR ABOUT YOU */}
        <div className={cn('item')}>
          <h2 className={cn('item__heading')}>
            Information We Collect From Or About You
          </h2>
          <p className={cn('item__paragraph')}>
            We collect and process different types of personal data via Our Website,
            which may include the following:
          </p>
          <ul className={cn('item__list')}>
            <li className={cn('item__list-item')}>
              <span className={cn('item__bold')}>Personal identifiers/details</span> - such as name,
              address, employer, job title, contact details (e.g. email and phone number),
              which you provide to us through our registration, enquiry or contact forms,
              direct interactions, or if you correspond with us via email and which we use:
              <ul className={cn('item__list')}>
                <li className={cn('item__list-item')}>
                  to fulfil any request you make (e.g. submitting a query on Our Website,
                  placing an order on our Website, subscribing to a membership or communications
                  service, fulfilling requests for information, entering into a competition,
                  promotion, survey etc.); and
                </li>
                <li className={cn('item__list-item')}>
                  to allow you to participate in any interactive features of our service,
                  when you choose to do so.
                </li>
              </ul>
            </li>
            <li className={cn('item__list-item')}>
              <span className={cn('item__bold')}>Marketing data</span>{' '}
              - such as to create a database of people who have expressed interest in our
              companies (subsidiaries or affiliates, including those within the DP World Group),
              products or services; including creating and maintaining a mailing list of people
              who have expressed an interest in receiving electronic marketing communications from us.
            </li>
            <li className={cn('item__list-item')}>
              <span className={cn('item__bold')}>Feedback and opinions</span>{' '}
              - which you provide to us including through our enquiry or contact forms is
              processed to allow us to answer queries and complaints you may have and to
              help us evaluate the quality and consistency of our services.
            </li>
            <li className={cn('item__list-item')}>
              <span className={cn('item__bold')}>Website and communication usage</span>{' '}
              - including your IP address, visits to Our Website, browser type and version,
              time zone setting, browser plug-in types and versions, operating system and
              platform and information about your visit, including the full Uniform Resource
              Locators (URL), clickstream to, through and from Our Website (including date and time),
              products or services you viewed or searched for, page response times, download errors,
              length of visits to certain pages, page interaction information (such as scrolling,
              clicks, and mouse-overs), methods used to browse away from the page and any phone
              number used to call our customer service number which we collect from your browser
              to enable us to monitor the performance and relevance of Our Website and our ad words
              campaigns, to ensure that content from Our Website is presented in the most effective
              manner for you and for your computer, and to keep Our Website safe and secure.
            </li>
            <li className={cn('item__list-item')}>
              <span className={cn('item__bold')}>Cookie usage</span>{' '}
              - Our Website uses cookies to distinguish you from other users. This helps us to
              provide you with a good experience when you browse Our Website and also allows us to improve it.
            </li>
          </ul>
          <p className={cn('item__paragraph')}>
            In addition, we use the information we collect to create profiles and to segment
            our database for analytical and direct marketing purposes including sending marketing
            and promotional materials. We may combine information we obtain from other sources
            with information we collect from Our Website to improve our business analysis and
            customer understanding.
          </p>
          <p className={cn('item__paragraph')}>
            If you join our communications program we collect information about each email
            we have sent to you and your interaction with them, including, without limitation,
            the number of times opened, deliverability, which sections you clicked on etc which
            we use to measure the effectiveness of our email campaigns, to plan marketing
            campaigns and to segment our database.
          </p>
          <p className={cn('item__paragraph')}>
            We may also undertake additional data processing activities but will ensure they
            are compatible with this Privacy Policy.
          </p>
        </div>

        {/* USE OF PERSONAL DATA */}
        <div className={cn('item')}>
          <h2 className={cn('item__heading')}>
            Use of Personal Data
          </h2>
          <p className={cn('item__paragraph')}>
            We will only use your personal data when the law allows us to.
            In the day to day running of our business we may use your personal data
            without asking for your consent because:
          </p>
          <ul className={cn('item__list')}>
            <li className={cn('item__list-item')}>
              we are entering into and carrying out our obligations under a contract with you;
            </li>
            <li className={cn('item__list-item')}>
              we need to use your personal data for our own legitimate interest purposes
              (such as the administration and management of our business and the improvement of our services)
              which shall comply with GDPR and your interests and fundamental rights do not override
              those interests; and
            </li>
            <li className={cn('item__list-item')}>
              where we need to comply with a legal or regulatory obligation.
            </li>
          </ul>
          <p className={cn('item__paragraph')}>
            Other types of legal basis that we rely on to process your personal data are
            included within this Privacy Policy.
          </p>
        </div>

        {/* CONSENT */}
        <div className={cn('item')}>
          <h2 className={cn('item__heading')}>
            Concent
          </h2>
          <p className={cn('item__paragraph')}>
            Whilst we always want you to be aware of how we are using your personal data,
            this does not necessarily mean that we are required to ask for your consent before we can use it.
          </p>
          <p className={cn('item__paragraph')}>
            If we choose to process your personal data for any purpose incompatible with those
            described in this section, we will provide you with appropriate information at the
            point where you come across those additional purposes in order to obtain your
            consent (where required) or are able to satisfy our legal obligations,
            prior to commencing any such additional processing activities. You are not required
            to give consent just because we ask for it. If you do give consent you can change
            your mind and withdraw it at a later date by contacting us and requesting to be
            removed from the mailing list on the following email address:{' '}
            <a
              className={cn('item__link')}
              href='mailto:data.protection@dpworld.com'
            >
              data.protection@dpworld.com
            </a>.
          </p>
          <p className={cn('item__paragraph')}>
            You are not under a legal obligation to provide us with any of your personal data
            but please note that if you elect not to provide us with your personal data we may
            be unable to provide our products or services to you.
          </p>
        </div>

        {/* RELATED WEBSITES */}
        <div className={cn('item')}>
          <h2 className={cn('item__heading')}>
            Related Websites
          </h2>
          <p className={cn('item__paragraph')}>
            We operate several other websites which may be accessed via Our Website each of
            which has privacy information informing you how personal data are handled through that website.
          </p>
          <p className={cn('item__paragraph')}>
            It is important that you read this Privacy Policy together with any other privacy
            notice we may provide on specific occasions when we are collecting or processing personal
            data about you so that you are fully aware of how and why we are using your data.
            This Privacy Policy supplements the other notices and is not intended to override them.
          </p>
          <p className={cn('item__paragraph')}>
            Our Website may include links to third party websites, plus-ins and applications.
            Clicking on those links or enabling those connections may allow third parties to collect
            or share data about you. We do not control these third party websites and are not
            responsible for their privacy statements. When you leave Our Website, we encourage
            you to read the privacy notice of every website you visit.
          </p>
        </div>

        {/* LEGAL BASIS FOR PROCESSING */}
        <div className={cn('item')}>
          <h2 className={cn('item__heading')}>
            Legal Basis For Processing
          </h2>
          <p className={cn('item__paragraph')}>
            The legal basis for processing personal data described above is the pursuit of
            our legitimate interests of seeking to promote, develop and grow our businesses
            through the sale of products and services and to provide excellent customer services.
          </p>
          <p className={cn('item__paragraph')}>
            If you subscribe to our email communications program we may send to you unsolicited
            direct email marketing materials but only where you have granted your consent or
            if they relate to products and services similar to those you have previously bought
            from us or expressed interest in.
          </p>
        </div>

        {/* DISCLOSING, SHARING AND TRANSFERRING PERSONAL DATA */}
        <div className={cn('item')}>
          <h2 className={cn('item__heading')}>
            Disclosing, Sharing And Transferring Personal Data
          </h2>
          <p className={cn('item__paragraph')}>
            We may disclose your personal information to other third parties from time to time:
            <ul className={cn('item__list')}>
              <li className={cn('item__list-item')}>
                to fulfil our obligations under agreement that we have with you;
              </li>
              <li className={cn('item__list-item')}>
                if we are under a duty to disclose or share your personal data to comply
                with any legal obligation or if we sell or buy any business or assets,
                in which case we will disclose your personal data to the prospective
                seller or buyer of such business or assets;
              </li>
              <li className={cn('item__list-item')}>
                to enforce or apply our terms of use and other agreements;
              </li>
              <li className={cn('item__list-item')}>
                to protect the rights, property, or safety of our company, our customers, or
                others including exchanging information with other companies and organisations
                for the purposes of fraud protection and credit risk reduction;
              </li>
              <li className={cn('item__list-item')}>
                business partners, suppliers and sub-contractors for the performance of any
                contract we enter into with them or you;
              </li>
              <li className={cn('item__list-item')}>
                analytics and search engine providers that assist us in the improvement and
                optimisation of Our Website;
              </li>
              <li className={cn('item__list-item')}>
                credit reference agencies for the purpose of assessing your credit score where
                this is a condition of us entering into a contract with you.
              </li>
              <li className={cn('item__list-item')}>
                advertisers and advertising networks that require the data to select and serve
                relevant adverts to you and others for the purposes of the third parties commercial
                interests and/or to enable us to comply with our advertisers' wishes by displaying
                their advertisement to that target audience.
              </li>
            </ul>
          </p>
          <p className={cn('item__paragraph')}>
            We will only share your personal data with third parties for marketing purposes
            if you provide us with your consent to do so including by email or by ticking a
            box on a form, or any other method that we use to collect your personal data.
          </p>
          <p className={cn('item__paragraph')}>
            We may also share information about you with other companies within the DP World
            Group where we think you may have an interest in their products and services.
            We undertake data sharing on the basis of our Group's legitimate interests in promoting
            our products and services and undertaking direct marketing.
          </p>
        </div>

        {/* COMMUNICATION METHODS */}
        <div className={cn('item')}>
          <h2 className={cn('item__heading')}>
            Communication Methods
          </h2>
          <p className={cn('item__paragraph')}>
            We may send you marketing communications by email, telephone, text message or post.
          </p>
          <p className={cn('item__paragraph')}>
            You can ask us to only send you marketing communications by particular methods
            (for example, you may be happy to receive emails from us but not telephone calls),
            about specific subjects or you may ask us not to send you any marketing communications at all.
          </p>
          <p className={cn('item__paragraph')}>
            We may ask you to indicate your marketing preferences when you first register an
            account on Our Website and/or app (as applicable), which you may change later on by
            emailing us on:{' '}
            <a
              className={cn('item__link')}
              href='mailto:data.protection@dpworld.com'
            >
              data.protection@dpworld.com
            </a>.
          </p>
        </div>

        {/* DATA RETENTION */}
        <div className={cn('item')}>
          <h2 className={cn('item__heading')}>
            Data Retention
          </h2>
          <p className={cn('item__paragraph')}>
            We will hold information about you in our data systems only for as long as we
            need to hold it which generally is as follows:
          </p>
          <ul className={cn('item__list')}>
            <li className={cn('item__list-item')}>
              We will retain and process information about you for as long as you continue
              to use or express an interest in our services, log into Our Website, use our
              interactive services, or for as long as we feel that our business, products
              and services may be of interest to you where you have not indicated otherwise.
            </li>
            <li className={cn('item__list-item')}>
              We will hold information about how you interact with our electronic communications
              for as long as they are relevant to helping us analyse the performance of our digital
              marketing activities or customers and prospects.
            </li>
          </ul>
          <p className={cn('item__paragraph')}>
            Please note that we may anonymise your personal data or use it for statistical
            purposes. We keep anonymised and statistical data indefinitely but we take care
            to ensure that such data can no longer identify or be connected to any individual.
          </p>
        </div>

        {/* YOUR RIGHTS */}
        <div className={cn('item')}>
          <h2 className={cn('item__heading')}>
            Your Rights
          </h2>
          <p className={cn('item__paragraph')}>
            Data protection law grants you certain rights ('information rights') which provide you with:
          </p>
          <ul className={cn('item__list')}>
            <li className={cn('item__list-item')}>
              <span className={cn('item__bold')}>Right of access and of data portability</span>{' '}
              - you have the right of access to information we hold about or concerning
              you and/or to have it transferred to another data controller in some circumstances.
              If you would like to exercise this right you should contact us as outlined above.
            </li>
            <li className={cn('item__list-item')}>
              <span className={cn('item__bold')}>Right of rectification or erasure</span>{' '}
              - if you feel that any data that we hold about you is inaccurate you have the
              right to ask us to correct or rectify it. You also have a right to ask us to
              erase information about you where you can demonstrate that the data we hold is
              no longer needed by us, or if you withdraw the consent upon which our processing
              is based, or if you feel that we are unlawfully processing your data. Your right
              of rectification and erasure extends to anyone we have disclosed your personal
              information to and we will/shall take all reasonable steps to inform those with
              whom we have shared your data about your request for erasure.
            </li>
            <li className={cn('item__list-item')}>
              <span className={cn('item__bold')}>Right to restriction of processing</span>{' '}
              - you have a right to request that we refrain from processing your data where
              you contest its accuracy, or the processing is unlawful and you have opposed
              ts erasure, or where we don't need to hold your data anymore but you need us
              to in order to establish, exercise or defend any legal claims, or we are in
              dispute about the legality of our processing your personal data.
            </li>
            <li className={cn('item__list-item')}>
              <span className={cn('item__bold')}>Right to object</span>{' '}
              - you have a right to object to our processing of your personal data where
              the basis of the processing is our legitimate interests including but not
              limited to direct marketing and profiling.
            </li>
            <li className={cn('item__list-item')}>
              <span className={cn('item__bold')}>Right to Withdraw Consent</span>{' '}
              - you have the right to withdraw your consent for the processing of your personal
              data where the processing is based on consent. To withdraw consent, please specify
              what you wish to withdraw consent for, by emailing:{' '}
              <a
                className={cn('item__link')}
                href='mailto:data.protection@dpworld.com'
              >
                data.protection@dpworld.com
              </a>.
            </li>
            <li className={cn('item__list-item')}>
              <span className={cn('item__bold')}>Right of Complaint</span>{' '}
              - if you are unhappy with the way we have used or are handling your personal data
              you have the right to lodge a complaint the supervisory authority for data protection
              issues. We would, however, appreciate the chance to deal with your concerns before
              you approach the Supervisory Authority so please contact us in the first instance
              by emailing:{' '}
              <a
                className={cn('item__link')}
                href='mailto:data.protection@dpworld.com'
              >
                data.protection@dpworld.com
              </a>.
            </li>
          </ul>
        </div>

        {/* DATA SECURITY */}
        <div className={cn('item')}>
          <h2 className={cn('item__heading')}>
            Data Security
          </h2>
          <p className={cn('item__paragraph')}>
            We take every care to ensure that your personal data is kept secure. We have put
            in place appropriate security measures to prevent your personal data from being
            accidentally lost, used or accessed in an unauthorised way, altered or disclosed.
            In addition, we limit access to your personal data to those employees, agents,
            contractors and other third parties who have a business need to know. They will
            only process your personal data on our instructions and they are subject to a duty
            of confidentiality.
          </p>
          <p className={cn('item__paragraph')}>
            We have put in place procedures to deal with any suspected personal data breach
            and will notify you and any applicable regulator of a breach where we are legally
            required to do so.
          </p>
          <p className={cn('item__paragraph')}>
            Please remember that you are responsible for keeping your passwords secure.
            If we have given you (or you have chosen) a password which enables you to access
            certain parts of Our Website or app (as applicable), you are responsible for keeping
            this password confidential. Please do not to share your passwords with anyone.
          </p>
          <p className={cn('item__paragraph')}>
            Unfortunately, sending information via the internet is not completely secure.
            Although we will do our best to protect your personal data, we cannot guarantee
            the security of personal data sent to Our Website; you send us personal data at
            your own risk. Once we have received your personal data, we will use strict
            procedures and security features to try to prevent unauthorised access.
          </p>
        </div>

        {/* HOW WE KEEP THIS POLICY UP TO DATE */}
        <div className={cn('item')}>
          <h2 className={cn('item__heading')}>
            How We Keep This Policy Up To Date
          </h2>
          <p className={cn('item__paragraph')}>
            We will review and update this privacy policy from time to time. This may be to reflect
            a change in the products or services we offer or to our internal procedures or it may
            be to reflect a change in the law.
          </p>
          <p className={cn('item__paragraph')}>
            The easiest way to check for updates is by looking for the latest version of this
            policy on Our Website or you can contact us by emailing:{' '}
            <a
              className={cn('item__link')}
              href='mailto:data.protection@dpworld.com'
            >
              data.protection@dpworld.com
            </a>.
          </p>
          <p className={cn('item__paragraph')}>
            Each time we update our privacy policy we will update the policy version number shown
            at the end of the privacy policy and the date on which that version of the privacy policy
            came into force.
          </p>
          <p className={cn('item__paragraph')}>
            This is policy version is Version 1 dated 19 May 2020.
          </p>
        </div>

      </div>
    </section>
  );
};

export default SectionContent;